import React, {
  createContext,
  useState,
  useRef,
  useEffect,
  useContext,
} from "react";
import { axiosInstance } from "../axiosInstance";
import { SettingsContext } from "./SettingsContext";
import { AuthContext } from "./AuthContext";
import config from "../config";
import { generateGreeting } from "../utils/generateGreeting";

export const ChatContext = createContext();

export function ChatProvider({ children }) {
  const { botName, greetUserAgainIfIdleFor } = config;
  const { firstName,  chefPersona } = useContext(SettingsContext);
  const { accessToken } = useContext(AuthContext);

  const [messages, setMessages] = useState([]);
  const [savedRecipes, setSavedRecipes] = useState([]);
  const hasInitialized = useRef(false); // ✅ React-safe persistent flag

  useEffect(() => {

    const initChat = async () => {
      const stored = localStorage.getItem("chatMessages");

      const lastActive = parseInt(localStorage.getItem("t_active") || "0", 10);
      const now = Date.now();
      const hasBeenAway = now - lastActive > greetUserAgainIfIdleFor * 1000;
      const timeOfDaygreeting = generateGreeting();
      const messageGreeting = firstName.length ? `Welcome back, ${firstName}` : "Welcome back";
      
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
  
          // Inject welcome back message only if time threshold passed
          if (hasBeenAway) {
            const welcomeBack = {
              sender: botName,
              text: `${messageGreeting}! ${timeOfDaygreeting}. Ready to dive back into your culinary adventure?`,
            };
            const updated = [...parsed, welcomeBack];
            setMessages(updated);
            localStorage.setItem("chatMessages", JSON.stringify(updated));
          } else {
            setMessages(parsed);
          }
  
          // Update activity timestamp
          localStorage.setItem("t_active", now.toString());
          return;
        } catch (err) {
          console.error("Failed to parse stored messages:", err);
        }
      }
      try {
        console.log("initChat: sending OpenAI request");
        const response = await axiosInstance.post("/api/chat/greeting", {
          chefPersona,
          botName,
        });
        console.log(
          "initChat: received OpenAI response:",
          response.data.greeting?.trim()
        );
        const greeting =
          response.data.greeting?.trim() ||
          "Hi there! Let’s plan a delicious and nutritious meal together. What type of cuisine are you interested in today? Ask me anything - for example, you can explore **vegetarian**, **Italian**, **breads**, **curry dishes**, **African** or any other type of food you have in mind.";
        const firstMessage = { sender: botName, text: greeting };
        setMessages([firstMessage]);
        localStorage.setItem("chatMessages", JSON.stringify([firstMessage]));
      } catch (err) {
        console.error("Error fetching greeting:", err);
        const fallback = {
          sender: botName,
          text: "Welcome! What would you like to cook today?",
        };
        setMessages([fallback]);
      }
    };

    if (!hasInitialized.current) {
      hasInitialized.current = true;
      initChat(); // ✅ runs only once, regardless of StrictMode
    }
  }, [chefPersona, firstName, botName, greetUserAgainIfIdleFor]);

  const syncStorage = (data, key) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
      console.error("Failed to sync localStorage:", key, e);
    }
  };

  const addMessage = (sender, text, tag) => {
    const typed = sender !== botName;
    setMessages((prevMessages) => {
      const newMessages = [...prevMessages, { sender, text, tag, typed }];
      syncStorage(newMessages, "chatMessages");
      return newMessages;
    });
    localStorage.setItem("t_active", Date.now().toString());
  };
  

  const saveRecipe = (recipe) => {
    const newRecipes = [...savedRecipes, recipe.recipeId];
    setSavedRecipes(newRecipes);
    syncStorage(newRecipes, "savedRecipes");
  };

  const setMessageTyped = (index) => {
    const clone = [...messages];
    clone[index] = { ...clone[index], typed: true };
    setMessages(clone);
    syncStorage(clone, "chatMessages");
  };

  const resetConversationLocally = () => {
    const responseOpener = firstName.length ? `Okay ${firstName}` : "Okay";
    const timeOfDaygreeting = generateGreeting();
    console.log("responseOpener = ", responseOpener);
    const reset = [
      {
        sender: botName,
        text: `${responseOpener}, ${timeOfDaygreeting}. Let's start over! What type of cuisine would you like to explore?`,
      },
    ];
    setMessages(reset);
    syncStorage(reset, "chatMessages");
    hasInitialized.current = false;
  };

  return (
    <ChatContext.Provider
      value={{
        messages,
        addMessage,
        savedRecipes,
        saveRecipe,
        resetConversationLocally,
        setMessageTyped,
        botName,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}
